/**
 * IDF Lexer (Tokenizer)
 *
 * Converts raw IDF text into a stream of tokens for the parser.
 * Handles comments, field values, and object delimiters.
 */

import { type Token, TokenType } from './types';

/**
 * Lexer for IDF (Input Data File) format.
 * Produces tokens for parsing EnergyPlus input files.
 */
export class IDFLexer {
  private input: string;
  private position: number = 0;
  private line: number = 1;
  private column: number = 0;
  private tokens: Token[] = [];

  constructor(input: string) {
    this.input = input;
  }

  /**
   * Tokenize the entire input and return all tokens.
   */
  tokenize(): Token[] {
    this.tokens = [];
    this.position = 0;
    this.line = 1;
    this.column = 0;

    while (!this.isAtEnd()) {
      this.scanToken();
    }

    // Add EOF token
    this.tokens.push({
      type: TokenType.EOF,
      value: '',
      line: this.line,
      column: this.column,
    });

    return this.tokens;
  }

  private isAtEnd(): boolean {
    return this.position >= this.input.length;
  }

  private peek(): string {
    if (this.isAtEnd()) return '\0';
    return this.input.charAt(this.position);
  }

  private advance(): string {
    const char = this.input.charAt(this.position);
    this.position++;

    if (char === '\n') {
      this.line++;
      this.column = 0;
    } else {
      this.column++;
    }

    return char;
  }

  private addToken(type: TokenType, value: string, line: number, column: number): void {
    this.tokens.push({ type, value, line, column });
  }

  private scanToken(): void {
    const startLine = this.line;
    const startColumn = this.column;
    const char = this.advance();

    switch (char) {
      case ',':
        this.addToken(TokenType.Comma, ',', startLine, startColumn);
        break;

      case ';':
        this.addToken(TokenType.Semicolon, ';', startLine, startColumn);
        break;

      case '!':
        this.scanComment(startLine, startColumn);
        break;

      case '\n':
        this.addToken(TokenType.Newline, '\n', startLine, startColumn);
        break;

      case '\r':
        // Handle CRLF
        if (this.peek() === '\n') {
          this.advance();
        }
        this.addToken(TokenType.Newline, '\n', startLine, startColumn);
        break;

      case ' ':
      case '\t':
        this.scanWhitespace(startLine, startColumn, char);
        break;

      default:
        // Start of a field value or class name
        this.scanValue(startLine, startColumn, char);
        break;
    }
  }

  private scanComment(startLine: number, startColumn: number): void {
    let comment = '!';

    // Check for !- style inline comment marker
    if (this.peek() === '-') {
      comment += this.advance();
    }

    // Read until end of line
    while (!this.isAtEnd() && this.peek() !== '\n' && this.peek() !== '\r') {
      comment += this.advance();
    }

    this.addToken(TokenType.Comment, comment, startLine, startColumn);
  }

  private scanWhitespace(startLine: number, startColumn: number, firstChar: string): void {
    let whitespace = firstChar;

    while (!this.isAtEnd() && (this.peek() === ' ' || this.peek() === '\t')) {
      whitespace += this.advance();
    }

    this.addToken(TokenType.Whitespace, whitespace, startLine, startColumn);
  }

  private scanValue(startLine: number, startColumn: number, firstChar: string): void {
    let value = firstChar;

    // Read until we hit a delimiter, comment, or newline
    while (!this.isAtEnd()) {
      const char = this.peek();

      // Stop at delimiters
      if (char === ',' || char === ';' || char === '!' || char === '\n' || char === '\r') {
        break;
      }

      value += this.advance();
    }

    // Trim trailing whitespace from the value
    const trimmedValue = value.trimEnd();

    // Determine if this is a class name or field value
    // Class names appear at the start of an object (after newlines/comments)
    // and are followed eventually by a comma
    // For now, we'll emit as FieldValue and let the parser determine context
    this.addToken(TokenType.FieldValue, trimmedValue, startLine, startColumn);
  }
}

/**
 * Convenience function to tokenize IDF content.
 */
export function tokenize(input: string): Token[] {
  const lexer = new IDFLexer(input);
  return lexer.tokenize();
}
